import { Button } from '@/components/atoms';
import { Typography } from '@/components/atoms/Typography';
import { UnitReservedConfettiIcon } from '@/components/atoms/icons';
import { UI_PRIMITIVES } from '@/core/constants/uiColors';
import { ALMETLAA_ACTION, ALMETLAA_SCREEN } from '@/core/constants/dynatraceCatalog';
import { useUIColors } from '@/core/hooks';
import { trackCatalogEvent } from '@/core/services/trackDynatraceCatalog';
import React, { useEffect } from 'react';
import { Modal, Pressable, StyleSheet, View } from 'react-native';
import { usePropertyLabels } from '../hooks/usePropertyLabels';

/**
 * Figma frame (Frame 184):
 * width 345, height 329, gap 32, radius 16, padding Spacing/6 (24).
 * Tight text gaps so the stack fits the fixed height.
 */
const FRAME = {
  width: 345,
  height: 329,
  radius: 16,
  pad: 24,
  gap: 32,
  iconSize: 50,
  /** Unit Reserved → Congratulations! */
  titleToCongrats: 4,
  /** Congratulations! → message */
  congratsToMessage: 4,
  buttonHeight: 49,
} as const;

export interface UnitReservedSuccessDialogProps {
  visible: boolean;
  onOk: () => void;
}

/**
 * Success dialog after confirming a unit reservation (web properties).
 */
export function UnitReservedSuccessDialog({
  visible,
  onOk,
}: Readonly<UnitReservedSuccessDialogProps>) {
  const colors = useUIColors();
  const { UNIT_DETAILS: labels } = usePropertyLabels();

  useEffect(() => {
    if (visible) {
      trackCatalogEvent({
        screen: ALMETLAA_SCREEN.RESERVE,
        actionName: ALMETLAA_ACTION.RESERVE.UNIT_RESERVED_SUCCESS_DIALOG_VIEW,
        eventType: 'view',
      });
    }
  }, [visible]);

  const handleOk = () => {
    trackCatalogEvent({
      screen: ALMETLAA_SCREEN.RESERVE,
      actionName: ALMETLAA_ACTION.RESERVE.UNIT_RESERVED_SUCCESS_PRIMARY_CLICK,
      eventType: 'action',
    });
    onOk();
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={handleOk}
    >
      <View style={styles.overlay}>
        <Pressable
          style={styles.backdrop}
          onPress={handleOk}
          accessibilityRole="button"
          accessibilityLabel={labels.UNIT_RESERVED_OK}
        />
        <View
          style={[styles.card, { backgroundColor: colors.backgroundLevel1 }]}
        >
          <UnitReservedConfettiIcon size={FRAME.iconSize} />

          <View style={styles.textBlock}>
            <Typography variant="title3" color="primary" style={styles.title}>
              {labels.UNIT_RESERVED_TITLE}
            </Typography>
            <Typography
              variant="body"
              color={colors.outlineSuccess}
              style={styles.congrats}
            >
              {labels.UNIT_RESERVED_CONGRATS}
            </Typography>
            <Typography variant="body" color="secondary" style={styles.message}>
              {labels.UNIT_RESERVED_MESSAGE}
            </Typography>
          </View>

          <Button
            title={labels.UNIT_RESERVED_OK}
            variant="primary"
            fullWidth
            onPress={handleOk}
            style={styles.okButton}
            textStyle={styles.okLabel}
          />
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: UI_PRIMITIVES.overlayBackdrop,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 16,
  },
  /** Sibling behind the card so web does not nest <button> inside backdrop Pressable. */
  backdrop: {
    ...StyleSheet.absoluteFillObject,
  },
  card: {
    width: FRAME.width,
    height: FRAME.height,
    borderRadius: FRAME.radius,
    padding: FRAME.pad,
    alignItems: 'center',
    /** Icon ↔ text block ↔ OK */
    gap: FRAME.gap,
    zIndex: 1,
  },
  textBlock: {
    alignItems: 'center',
    width: '100%',
  },
  /** Font/Size/L Medium / LH 100% / center */
  title: {
    lineHeight: 20,
    textAlign: 'center',
    width: '100%',
    marginBottom: FRAME.titleToCongrats,
  },
  /** Font/Size/Base Regular / LH 22 / center */
  congrats: {
    lineHeight: 22,
    textAlign: 'center',
    width: '100%',
    marginBottom: FRAME.congratsToMessage,
  },
  message: {
    lineHeight: 22,
    textAlign: 'center',
    width: '100%',
  },
  okButton: {
    height: FRAME.buttonHeight,
    borderRadius: 4,
    alignSelf: 'stretch',
  },
  okLabel: {
    fontSize: 16,
    lineHeight: 16,
    textAlign: 'center',
  },
});

export default UnitReservedSuccessDialog;
